<template>
  <div class="f-h problems" :class="mainElClasses">
    <FiltersZone
      v-if="!asWidget && !(asComponent && hideHeader)"
      :force-open="forceOpenOptions"
      :disabled="forceOpenOptions"
      :modal-title="$t('message.OPTIONS')"
    >
      <div slot="shortcuts">
        <h2 v-if="asComponent" class="basic-sub-title">
          {{ pageTitle }}
        </h2>
        <slot name="filters-shortcut" />
      </div>

      <button
        class="generic-border generic-border--text-btn generic-border--blue-border"
        @click="excelExport"
      >
        <Icon>{{ downloadMedium }}</Icon>
        <span>{{ $t('message.EXPORT_TO_EXCEL') }}</span>
      </button>
      <div class="field-hint mt10 mb10">
        {{ $t('message.PB_EXPORT_ALL') }}
      </div>
      <ComponentsList
        ref="options"
        :value="pbOptions"
        @edit="paramsEdit"
      />
    </FiltersZone>
    <RefreshOnscrollList
      ref="refList"
      class="problems__list f-h-below-filters"
      :class="{ 'problems__list--full-height': hideHeader }"
      @scrollBottom="refresh(true)"
      @scroll="handleScroll"
    >
      <div>
        <div v-if="!asWidget && !asComponent" class="problems__col-headers v-centerer">
          <SortOption
            class="problems__col-headers__problem"
            :label="$t('message.PROBLEM')"
            :is-current="pbOptSortBy.value === 0"
            :is-desc="pbOptSortBy.value === 0 && pbOptSortBy.desc"
            sortable
            @click.native="clickedSortOption(0)"
          />
          <SortOption class="problems__col-headers__tags" :label="$t('message.TAGS')" />
          <SortOption class="problems__col-headers__leader" :label="$t('message.LEADER')" />
          <SortOption
            class="problems__col-headers__deadline"
            :label="$t('message.DEADLINE')"
            :is-current="pbOptSortBy.value === 1"
            :is-desc="pbOptSortBy.value === 1 && pbOptSortBy.desc"
            sortable
            @click.native="clickedSortOption(1)"
          />
          <SortOption
            class="problems__col-headers__impact"
            :label="$t('message.IMPACT')"
            :is-current="pbOptSortBy.value === 2"
            :is-desc="pbOptSortBy.value === 2 && pbOptSortBy.desc"
            sortable
            @click.native="clickedSortOption(2)"
          />
          <SortOption class="problems__col-headers__state" :label="$t('message.STATE')" />
          <SortOption
            v-if="!noMenu"
            class="problems__col-headers__archive"
            :label="archives ? $t('message.RESTORE') : $t('message.ARCHIVE')"
          />
          <SortOption v-if="!noMenu" class="problems__col-headers__delete" :label="$t('message.DELETE')" />
        </div>
        <ProblemItem
          v-for="problem in sortedProblems"
          ref="problemItems"
          :key="problem.id"
          :problem="problem"
          :scrolling="scrolling"
          :no-route-link="preventRouteLinks"
          :no-menu="noMenu"
          :no-delete-btn="noDeleteBtn"
          @removed="refresh()"
          @click="itemClicked(problem)"
        />
      </div>
    </RefreshOnscrollList>
  </div>
</template>

<script>
/**
 * List of problems used as a router page
 *
 *
 * @vue-prop {Boolean} asComponent - 'Problems' is used as a component and not a router page
 * @vue-prop {Boolean} hideFiltersZone - If asComponent is true, hide the FiltersZone
 * @vue-prop {Boolean} asWidget - 'Problems' is also used as a component but more specifically as a widget
 *                                If true, all props below will be used to refresh, sort and filter the list
 *                                If false, properties below have no effect
 * @vue-prop {Boolean} noRouteLink - If true, items won't send a route change to the problem sheet
 * @vue-prop {Boolean} noMenu - If true, items won't have a menu button
 * @vue-prop {Boolean} noDeleteBtn - If true, or if this.noMenu, hide the delete buttons of items
 * @vue-prop {Boolean} hideHeader - If true, Hide the page head
 * @vue-prop {Boolean} archives - If true, fetch archived problems
 * @vue-prop {Object} widgetTypeFilters - Refresh params for filtering according to types
 * @vue-prop {Object} widgetZoneFilter - Refresh params for filtering according to zones
 * @vue-prop {Object} widgetImpactFilter - Refresh params for filtering according to the impacy
 * @vue-prop {Object} widgetSortBy - Refresh params for sorting
 *
 * @vue-event {Object} itemClicked - Is 'asComponent' or 'asWidget' AND noRouteLink are true, Clicking an item
 *                                   will trigger this event instead of routing to the problem sheet
 */

import $ from 'jquery';
import toastr from 'toastr';
import { cloneDeep, isEqual } from 'lodash';
// Vuex
import { FETCH_FILTERED_PROBLEM } from '@/store/modules/problem/action-types';
// Import components
import ComponentsList from '@/components/components-list.vue';
import FiltersZone from '@/components/filters-zone.vue';
import Icon from '@/components/icon.vue';
import ProblemItem from '@/components/problem-item.vue';
import RefreshOnscrollList from '@/components/refresh-onscroll-list.vue';
import SortOption from '@/components/sort-option.vue';
import mixinPbOptions from '@/mixins/page-options/problems.vue';
// Import socket handling utils
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import SocketMessages from '@/utils/classes/SocketMessages';
// Import utils
import { downloadMedium } from '@/assets/icons';
import { itemWidthClasses } from '@/utils/main';
import { exportListToExcel, parseImpactFilterParam } from '@/utils/problem-utils';
import IAAnalyser from '@/utils/classes/IAAnalyser';
import IAHelp from '@/utils/classes/IAHelp';
import IAHelpStep from '@/utils/classes/IAHelpStep';

const REQ_ITEMS_COUNT = 20;

export default {

  name: 'Problems',

  components: {
    ComponentsList,
    FiltersZone,
    Icon,
    ProblemItem,
    RefreshOnscrollList,
    SortOption,
  },

  mixins: [mixinSocketHandler, mixinPbOptions],

  props: {
    asComponent: Boolean,
    hideFiltersZone: Boolean,
    noDeleteBtn: Boolean,
    noMenu: Boolean,
    noRouteLink: Boolean,
    asWidget: Boolean,
    hideHeader: Boolean,
    allUserTeams: Boolean,
    archives: Boolean,
    widgetTypeFilters: {
      type: Object,
      default: () => ({}),
    },
    widgetZoneFilter: {
      type: Object,
      default: () => ({}),
    },
    widgetImpactFilter: {
      type: [Object, Array],
      default: () => ({ min: 0, max: 3 }),
    },
    widgetSortBy: {
      type: Object,
      default: () => ({ value: 3, desc: true }),
    },
    widgetLeaders: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      downloadMedium,
      elClasses: {},
      // Sorting
      itemsLimitOffset: 0,
      sqlLimit: REQ_ITEMS_COUNT,
      // Problems
      // problems: [],
      demoProblems: [],
      scrollFalseTimeout: null,
      scrolling: false,
      // Multiple refresh at once might result as duplicate problems (monkey-tests-proof)
      refreshing: false,
      forceOpenOptions: false,
      // Properties used by other components. See src/views/readme.md
      pageTitle: this.$t('message.PROBLEMS'),
      iaAnalysisText: this.$t('message.ANALYSING'),
      iaAnalysisBtns: [],
      iaTeachText: `<p>${this.$t('message.TEACH_PROBLEM_REPORTING')}</p>\
        <p>${this.$t('message.DAILY_MGT_MEANS_DAILY_PROBLEMS')}`,
      iaTeachBtns: [this.teacherBtn('NO_BLAME'), this.teacherBtn('SPOT_PB')],
    };
  },

  computed: {
    mainElClasses() {
      return {
        ...this.elClasses,
        'problems--as-widget': this.asWidget,
        'problems--as-component': this.asComponent,
      };
    },
    /** Array: Problems from the state */
    problems() {
      const fetchConfig = this.$store.state.problem.problemsFetchConfigurations[JSON.stringify(this.fetchParams)];
      const problemIds = fetchConfig ? fetchConfig.problemIds : [];
      return problemIds.map(id => this.$store.state.problem.problems[`${id}`]);
    },
    /** Returns a sorted array according to current sorting & filters options */
    sortedProblems() {
      return this.sortProblems(this.App.isOnDemoMode ? this.demoProblems : this.problems);
    },
    /**
     * If this is used as a component
     * Prevent route changes as a click should send an event to the parent
     * @return {Boolean} Whether items should trigger route changes
     */
    preventRouteLinks() {
      return this.noRouteLink;
    },
    // ========= Config options =========
    pbOptSortBy() {
      return {
        value: typeof this.$route.query.sortBy === 'undefined' ? 3 : Number(this.$route.query.sortBy),
        desc: this.$route.query.sortDesc !== 'false',
      };
    },
    /** Return the ids of types that should be selected */
    pbOptSelectedTypes() {
      const queryIds = this.$route.query.typesFilter;
      return !queryIds || queryIds === '' ? [] : queryIds.split(',').map(t => Number(t));
    },
    pbOptTypesInclusive() {
      return this.$route.query.typesInclusive === 'true';
    },
    pbOptZoneInclusive() {
      return this.$route.query.zoneInc === 'true';
    },
    pbOptZoneSelected() {
      const queryIds = this.$route.query.zoneFilter;
      return !queryIds || queryIds === '' ? [] : queryIds.split(',').map(z => Number(z));
    },
    pbOptImpactFilter() {
      const vals = this.$route.query.impactFilter;
      return !vals || vals === '' ? [0, 1, 2, 3] : vals.split(',').map(z => Number(z));
    },
    pbOptSelectedLeaders() {
      const queryIds = this.$route.query.problemsLeaders;
      if (queryIds === '') {
        return [];
      }
      return queryIds === undefined ? [] : queryIds.split(',').map(e => Number(e));
    },
    /** Object: Object used to specify the XHR parameters when fetching problems */
    fetchParams() {
      const params = {
        allTeamsOfUser: this.allUserTeams && this.currentUser && this.currentUser.id,
        archived: this.archives,
        types: this.pbOptSelectedTypes,
        typesInclusive: this.pbOptTypesInclusive,
        zones: this.pbOptZoneSelected,
        zonesInclusive: this.pbOptZoneInclusive,
        impact: parseImpactFilterParam(this.pbOptImpactFilter),
        sortBy: { index: this.pbOptSortBy.value, desc: this.pbOptSortBy.desc },
        leaders: this.pbOptSelectedLeaders,
      };
      if (this.asWidget) {
        params.types = this.widgetTypeFilters.types;
        params.typesInclusive = this.widgetTypeFilters.inclusive;
        params.zones = this.widgetZoneFilter.zones;
        params.zonesInclusive = this.widgetZoneFilter.inclusive;
        params.impact = parseImpactFilterParam(this.widgetImpactFilter);
        params.sortBy = { index: this.widgetSortBy.value, desc: this.widgetSortBy.desc };
        params.leaders = this.widgetLeaders;
      }
      return params;
    },
  },

  watch: {
    $route() {
      if (!this.asWidget) {
        this.$nextTick(this.refresh);
      }
    },
  },

  created() {
    this.refresh();
    $(window).on(`resize.${this.uuid}`, this.onSizeChanged);
  },

  mounted() {
    this.registerSocketHandlers();
    this.onSizeChanged();
  },

  beforeDestroy() {
    $(window).off(`resize.${this.uuid}`);
  },

  methods: {
    parseImpactFilterParam,
    /** Handle socket messages for problem updates */
    registerSocketHandlers() {
      this.handleSocketMessage(SocketMessages.CC_MSGS.PROBLEMS, () => {
        this.refresh();
      });
      this.handleSocketMessage(SocketMessages.CC_MSGS.PROBLEMS_AND_PLANS, () => {
        this.refresh();
      });
      this.handleSocketMessage(SocketMessages.CC_MSGS.PENDING_AND_PROBLEMS, () => {
        this.refresh();
      });
      this.handleSocketMessage(SocketMessages.CC_MSGS.PROBLEM_SHEET, () => {
        this.refresh();
      });
    },
    /** Refresh the page on created and on scroll to bottom */
    refresh(increaseLimit) {
      if (this.refreshing) {
        setTimeout(() => {
          this.refresh(increaseLimit);
        }, 100);
        return;
      }
      this.refreshing = true;
      const params = { ...this.fetchParams };
      params.limit = { offset: this.itemsLimitOffset, count: this.sqlLimit };
      if (increaseLimit !== true) {
        params.limit.count = this.itemsLimitOffset || REQ_ITEMS_COUNT;
        params.limit.offset = 0;
      }

      this.$store.dispatch(`problem/${FETCH_FILTERED_PROBLEM}`, params)
        .then((d) => {
          this.refreshing = false;
          this.itemsLimitOffset = this.problems.length;
          this.sqlLimit = REQ_ITEMS_COUNT;
          // this.problems = this.problems.concat(problems);
          this.analyse();
          this.$nextTick(() => {
            if (d.length > 0 && this.$refs.refList) {
              setTimeout(this.$refs.refList.checkHeight, 500);
            }
          });
        })
        .catch(() => {
          // Nothing to do. Was added for unit test
        });
    },
    /** Replace GET parameters in query when componentsList is edited */
    paramsEdit(option) {
      const query = Object.assign({}, this.$route.query);
      if (option.key === 'sortBy') {
        query.sortBy = option.value.value;
        query.sortDesc = option.value.desc ? 'true' : 'false';
      } else if (option.key === 'impactFilter') {
        query.impactFilter = option.value.join(',');
      } else if (option.key === 'zoneInclusive') {
        query.zoneInc = option.value ? 'true' : 'false';
      } else if (option.key === 'typesInclusive') {
        query.typesInclusive = option.value ? 'true' : 'false';
      } else if (option.key === 'selectedTypes') {
        query.typesFilter = option.value.length ? option.value.join(',') : undefined;
      } else if (option.key === 'zoneFilter') {
        query.zoneFilter = option.value.length ? option.value.join(',') : undefined;
      } else if (option.key === 'problemsLeaders') {
        query.problemsLeaders = option.value.join(',');
      }
      if (!isEqual(query, this.$route.query)) {
        this.$router.replace({ query });
      }
    },
    /** A column header has been clicked to sort the list of problems */
    clickedSortOption(optionIdx) {
      const option = { key: 'sortBy', value: { value: optionIdx, desc: true } };
      if (this.pbOptSortBy.value === optionIdx) {
        option.value.desc = !this.pbOptSortBy.desc;
      }
      this.paramsEdit(option);
    },
    sortProblems(problems) {
      const { value, desc } = this.asWidget ? this.widgetSortBy : this.pbOptSortBy;
      switch (value) {
        case 0:
          return !desc
            ? problems.sort((a, b) => a.lWhat.localeCompare(b.lWhat))
            : problems.sort((a, b) => b.lWhat.localeCompare(a.lWhat));
        case 1:
          return !desc
            ? problems.sort((a, b) => a.deadlineSortable - b.deadlineSortable)
            : problems.sort((a, b) => b.deadlineSortable - a.deadlineSortable);
        case 2:
          return !desc
            ? problems.sort((a, b) => a.impact - b.impact)
            : problems.sort((a, b) => b.impact - a.impact);
        case 3:
          return !desc
            ? problems.sort((a, b) => a.createdSortable - b.createdSortable)
            : problems.sort((a, b) => b.createdSortable - a.createdSortable);
        case 4:
          return !desc
            ? problems.sort((a, b) => a.updateSortable - b.updateSortable)
            : problems.sort((a, b) => b.updateSortable - a.updateSortable);
        default:
          return problems;
      }
    },
    /** Handle scroll to hide problem items menus when scrolling */
    handleScroll() {
      if (this.scrollFalseTimeout != null) {
        clearTimeout(this.scrollFalseTimeout);
      }
      this.scrolling = true;
      this.scrollFalseTimeout = setTimeout(() => {
        this.scrolling = false;
      }, 50);
    },
    /** Should be triggered only if noRouteLink is true */
    itemClicked(problem) {
      if (this.noRouteLink && this.asComponent) {
        this.$emit('itemClick', problem);
      }
    },
    analyse() {
      IAAnalyser.analyseProblems(this, (msg) => {
        this.iaAnalysisText = msg;
      }, this.problems);
    },
    /** Fetch all the problems and export them in a readable format */
    excelExport() {
      this.$http.get('/server/problems/export').then((response) => {
        if (!response || !(response.data instanceof Array)) {
          toastr.error(this.$t('message.CONNECTION_ERROR'));
          return;
        }
        exportListToExcel(response.data);
      });
    },
    /** Style of the problem is determined by the size of $el. Apply classes */
    onSizeChanged(callOnChildren) {
      const { refList, problemItems } = this.$refs;
      const elt = (problemItems && problemItems[0] && problemItems[0].$el) || refList.$el;
      const width = elt.offsetWidth;
      this.elClasses = itemWidthClasses
        .reduce((classes, wc) => ({ ...classes, [wc.className]: width <= wc.width }), {});

      if (callOnChildren) {
        problemItems.forEach((pbComponent) => {
          if (pbComponent && pbComponent.onSizeChanged) {
            pbComponent.onSizeChanged();
          }
        });
      }
    },
    createHelpSteps() {
      this.makeDemoValues();
      return [
        new IAHelpStep(null, this.$t('help.PB_INTRO')),
        new IAHelpStep('.problem-item', this.$t('help.PB_ITEM')),
        new IAHelpStep('.problem-item .problem-item__description', this.$t('help.PB_ITEM_STATE'), (clickEvt) => {
          clickEvt.preventDefault();
          clickEvt.stopPropagation();
          $('.problem-item .pbi-menu').first().trigger('click');
        }),
        new IAHelpStep('.problem-item__delete', this.$t('help.PB_ITEM_DELETE')),
        new IAHelpStep('.problems .filters-zone', this.$t('help.PEND_SORT'), (e, next) => {
          this.forceOpenOptions = true;
          this.$nextTick(next);
          return true;
        }),
        new IAHelpStep('.problems .filters-zone__content', this.$t('help.PEND_SORT_CHOOSE'), () => {
          this.forceOpenOptions = false;
        }),
      ];
    },
    makeDemoValues() {
      this.demoProblems = IAHelp.demoProblems(this);
    },
    userLoaded() {
      // Refresh necesary data for problems filters
      this.pbOptUserLoaded();
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";
@import "@/assets/scss/page.scss";

.problems {

  &--as-widget, &--as-component {
    .problems__list {
      padding: 0 .2rem;

      &.f-h-below-filters {
        height: 100%;
      }
    }

    .filters-zone__buttons {
      border-bottom: 1px solid $color-gray-border;
      border-top-left-radius: $main-border-radius;
      border-top-right-radius: $main-border-radius;
    }

    .problem-item {
      border: 1px solid $color-gray-border;
    }
  }

  &__col-headers {
    margin-top: 0.8em;
    height: 1.8em;

    & &__problem {
      margin-left: calc(#{$problem-item-height} + 0.7rem);
      flex: 1;
      justify-content: unset;
    }

    &__leader {
      flex: 0 0 7.5rem;
    }

    &__deadline {
      // Width = 6em + margins of 4rem
      flex: 0 0 10rem;
      text-align: left;
    }

    &__impact {
      // Width = 6em + margins of 4rem
      flex: 0 0 12rem;
    }

    &__state {
      // Width = 8em + margins of 4rem
      flex: 0 0 12rem;
    }

    &__tags {
      // Margin: 1.43rem, width: 4rem
      flex: 0 0 5.86rem;
    }

    &__archive {
      flex: 0 0 5.72rem;
    }

    &__delete {
      // Delete button has 20px left, 20px rightand is centered. 20px = 1.43rem
      flex: 0 0 5.72rem;
    }
  }

  & &__list {
    padding-top: 0px;

    &--full-height {
      height: 100%;
    }
  }

  &.tablet-size {
    .problems {
      &__col-headers {
        &__leader {
          flex: 0 0 4rem;
        }
        &__deadline {
          flex: 0 0 6.5rem;
        }
        &__impact {
          flex: 0 0 8.5rem;
        }
        &__state {
          flex: 0 0 11.5rem;
        }
        &__tags {
          flex: 0 0 4.86rem;
        }
        &__archive {
          flex: 0 0 4.72rem;
        }
        &__delete {
          flex: 0 0 4.72rem;
        }
      }
    }
  }

  &.sm-landscape-size {
    .problems {
      &__col-headers {
        &__leader {
          flex: 0 0 3rem;
        }
        &__deadline {
          flex: 0 0 6.5rem;
        }
        &__impact {
          flex: 0 0 4rem;
          margin-right: .3rem;
        }
        &__state {
          display: none;
        }
        &__tags {
          display: none;
        }
        &__archive {
          flex: 0 0 3.72rem;
        }
        &__delete {
          flex: 0 0 3.72rem;
        }
      }
    }
  }

  &.sm-portrait-size {
    .problems {
      &__col-headers {
        &__impact {
          flex: 0 0 4rem;
        }
        &__deadline, &__leader {
          display: none;
        }
      }
    }
  }

  &.sm-small-portrait-size {
    .problems {
      &__col-headers {
        &__impact {
          display: none;
        }
      }
    }
  }
}
</style>
